import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Service to manage call feature availability across the application.
 * This service controls whether call functionality is enabled based on company settings.
 */
@Injectable({
  providedIn: 'root'
})
export class CallFeatureService {
  private callsEnabledSubject = new BehaviorSubject<boolean>(false);
  public callsEnabled$: Observable<boolean> = this.callsEnabledSubject.asObservable();

  constructor() {
    // Load initial state from localStorage
    this.loadCallFeatureStatus();
  }

  /**
   * Load call feature status from localStorage
   * Called on service initialization
   */
  private loadCallFeatureStatus(): void {
    try {
      const companyData = localStorage.getItem('companyData');
      if (companyData) {
        const company = JSON.parse(companyData);
        const enabled = company.enable_calls === true;
        this.callsEnabledSubject.next(enabled);
      } else {
        console.warn('📞 No company data found in localStorage, calls disabled by default');
        this.callsEnabledSubject.next(false);
      }
    } catch (error) {
      console.error('📞 Error loading call feature status:', error);
      this.callsEnabledSubject.next(false);
    }
  }

  /**
   * Update call feature status
   * Called when admin changes the setting or when company data is refreshed
   * @param enabled Whether calls should be enabled
   */
  public updateCallFeatureStatus(enabled: boolean): void {
    this.callsEnabledSubject.next(enabled);
    // Update in localStorage
    try {
      const companyData = localStorage.getItem('companyData');
      if (companyData) {
        const company = JSON.parse(companyData);
        company.enable_calls = enabled;
        localStorage.setItem('companyData', JSON.stringify(company));
      }
    } catch (error) {
      console.error('📞 Error updating call feature status in localStorage:', error);
    }
  }

  /**
   * Get current call feature status synchronously
   * @returns true if calls are enabled, false otherwise
   */
  public isCallsEnabled(): boolean {
    return this.callsEnabledSubject.value;
  }

  /**
   * Refresh call feature status from localStorage
   * Useful after company data is updated
   */
  public refreshStatus(): void {
    this.loadCallFeatureStatus();
  }
}
